import moment from 'moment/moment';
import { Interval, Rollup } from '../hooks/useTimeState';

export const convertToUtc = (date: Date) =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    )
  );

export const formatDateString = (date: Date) => {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

export const formatTimeString = (date: Date) => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
  });
};

export const formatDateOnlyStringLong = (date: Date, year?: boolean) => {
  const dateString = date.toLocaleString('en-us', {
    dateStyle: 'long',
  });
  return year ?? false ? dateString : dateString.split(',')[0].trim();
};

export const formatDateStringWithTimeZone = (date: Date) => {
  return date.toLocaleString('en-us', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

export const formatDateStringWithTimeZoneAndSeconds = (date: Date) => {
  return date.toLocaleString('en-us', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
};

export const startOfDay = (date: Date) => {
  return new Date(date.setHours(0, 0, 0, 0));
};

export const endOfDay = (date: Date) => {
  return new Date(date.setHours(23, 59, 59, 59));
};

export const setSameDateAsRange = (date: Date): DateRange => {
  return {
    startDate: startOfDay(date),
    endDate: endOfDay(date),
  };
};

export const setDateRange = (startDate: Date, endDate: Date): DateRange => {
  return {
    startDate: startOfDay(startDate),
    endDate: endOfDay(endDate),
  };
};

export const addSecondsToDate = (date: Date, secondsToAdd: number) => {
  return new Date(date.setSeconds(date.getSeconds() + secondsToAdd));
};

export const addMinutesToDate = (date: Date, minutesToAdd: number) => {
  return new Date(date.setMinutes(date.getMinutes() + minutesToAdd));
};

export const addHoursToDate = (date: Date, hoursToAdd: number) => {
  return new Date(date.setHours(date.getHours() + hoursToAdd));
};

export const addDaysToDate = (date: Date, daysToAdd: number): Date => {
  return new Date(date.setDate(date.getDate() + daysToAdd));
};

export const addMonthsToDate = (date: Date, monthsToAdd: number): Date => {
  return new Date(date.setMonth(date.getMonth() + monthsToAdd));
};

export const getDayInWeek = (date: Date): Date => {
  return new Date(date.setDate(date.getDate() + new Date().getDay()));
};

export const startOfWeek = (date: Date) => {
  const dayDiff = date.getDay() === 0 ? -6 : 0;
  const startOfWeek = date.getDate() - date.getDay() + dayDiff;

  return new Date(date.setDate(startOfWeek));
};

export const endOfWeek = (date: Date) => {
  const dayDiff = date.getDay() === 0 ? 6 : 0;
  const endOfWeek = date.getDate() - date.getDay() + dayDiff + 6;

  return new Date(date.setDate(endOfWeek));
};

export const getDateInMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), new Date().getDate());
};

export const getSunday = (date: Date): Date => {
  const diff = date.getDate() - date.getDay();
  return new Date(date.setDate(diff));
};

export const getSaturday = (date: Date): Date => {
  const diff = date.getDate() + (6 - date.getDay());
  return new Date(date.setDate(diff));
};

export const getFirstOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

export const getLastOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

export const getTimeOffset = () => {
  let offsetHours = new Date().getTimezoneOffset() / 60;
  let minutes = 0;
  let offsetSymbol = '-';

  if (offsetHours % 1 !== 0) minutes = 30;
  if (offsetHours < 0) {
    offsetHours = offsetHours * -1;
    offsetSymbol = '';
  }

  return `${offsetSymbol}${String(offsetHours).padStart(2, '0')}:${String(
    minutes
  ).padStart(2, '0')}`;
};

export type DateRangeType =
  | 'Today'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'Last 30 Days'
  | 'Last 90 Days';
export const DateRangeMap: Record<DateRangeType, DateRange> = {
  ['Today']: {
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  },
  ['Yesterday']: {
    startDate: addDaysToDate(startOfDay(new Date()), -1),
    endDate: addDaysToDate(endOfDay(new Date()), -1),
  },
  ['Last 7 Days']: {
    startDate: addDaysToDate(startOfDay(new Date()), -6),
    endDate: endOfDay(new Date()),
  },
  ['Last 30 Days']: {
    startDate: addDaysToDate(startOfDay(new Date()), -29),
    endDate: endOfDay(new Date()),
  },
  ['Last 90 Days']: {
    startDate: addDaysToDate(startOfDay(new Date()), -89),
    endDate: endOfDay(new Date()),
  },
};

type IntervalOptions = {
  startDate: Date;
  endDate: Date;
  intervals: Interval[];
  rollup?: Rollup[];
};

export function getIntervals(options: IntervalOptions) {
  const MAX_DATA_POINTS: number = 600;
  options.intervals.forEach((i) => {
    if (i.dataPoints > MAX_DATA_POINTS) i.dataPoints = MAX_DATA_POINTS;
  });

  const totalSeconds =
    (options.endDate.getTime() - options.startDate.getTime()) / 1000;

  return options.intervals
    .map((i) => {
      let interval = totalSeconds / i.dataPoints;
      let rollupInterval;

      if (options.rollup) {
        for (let i = 0; i < options.rollup.length; i++) {
          const rollup = options.rollup[i];
          const timeframeSeconds = intervalToSeconds(rollup.timeframe);

          if (
            options.startDate <
              new Date(new Date().getTime() - timeframeSeconds * 1000) &&
            interval < intervalToSeconds(rollup.minInterval)
          ) {
            rollupInterval = rollup.minInterval;
          }
        }
      }

      if (totalSeconds <= 60 * 5) {
        interval = Math.ceil(interval);
      } else if (totalSeconds < 60 * 15) {
        interval = Math.ceil(interval);
      } else if (
        totalSeconds >= 60 * 15 &&
        (interval % 5 !== 0 || interval % 10 !== 0)
      ) {
        interval = Math.ceil(interval / 5) * 5;
      } else if (totalSeconds >= 60 * 15 && interval % 10 !== 0) {
        interval = Math.ceil(interval / 60) * 60;
      }

      return rollupInterval ?? secondsToInterval(interval);
    })
    .reduce((acc: string[], i: string) => {
      if (acc.indexOf(i) === -1) {
        acc.push(i);
      }
      return acc;
    }, []);
}

export function intervalToSeconds(interval: string): number {
  const suffix = interval.substring(interval.length - 1);
  const intervalNum = parseInt(interval);

  if (suffix === 's') return intervalNum;
  else if (suffix === 'm') return intervalNum * 60;
  else if (suffix === 'h') return intervalNum * 60 * 60;
  else if (suffix === 'd') return intervalNum * 60 * 60 * 24;
  else if (suffix === 'W') return intervalNum * 7 * 60 * 60 * 24;
  else if (suffix === 'M') return intervalNum * 30.437 * 60 * 60 * 24;
  else if (suffix === 'y') return intervalNum * 365 * 60 * 60 * 24;

  console.warn(`An "${interval}" is not a valid interval`);
  return -1;
}

function secondsToInterval(seconds: number): string {
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  else if (seconds >= 60 && seconds < 60 * 60) {
    if (seconds < 60 * 5) {
      return `${Math.ceil(Math.ceil(seconds / 60))}m`;
    }
    return `${Math.ceil(Math.ceil(seconds / 60) / 5) * 5}m`;
  } else if (seconds >= 60 * 60 && seconds < 60 * 60 * 24)
    return `${Math.ceil(seconds / (60 * 60))}h`;
  else return `${Math.ceil(seconds / (60 * 60 * 24))}d`;
}

export function validateInterval(interval: string): boolean {
  return /^[1-9][0-9]*(s|m|h|d|M|y)$/g.test(interval);
}
